import { FormEvent, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CircleAlert,
  Eye,
  EyeOff,
  LoaderCircle,
  Lock,
  Mail,
} from "lucide-react";

import AuthService from "../auth/Authorization.service";
import AuthorizationService from "../../sdk/services/AuthService";
import { Usuario } from "../../sdk/@types";
import useUsuario from "../../core/hooks/UseUsuario";
import { useProfileStore } from "../../core/store/useProfileStore";
import { hasProfile, PAID, type Profile } from "../auth/accessPolicy";
import AuthFrame from "../components/PublicChrome/AuthFrame";
import "./Login.view.css";

function loginPayload(
  identifier: string,
  password: string,
): { email: string; userName: string; password: string } {
  // email and userName are the same typed identifier, because that is the body getToken already sends.
  return { email: identifier, userName: identifier, password };
}

function isEmailNotVerified(error: unknown): boolean {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)
    ?.response?.data?.code;
  return code === "EMAIL_NOT_VERIFIED";
}

export default function LoginView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useUsuario();
  const setProfile = useProfileStore((state) => state.set);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [credentialError, setCredentialError] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [storedMessage, setStoredMessage] = useState(
    () => window.localStorage.getItem("errorMessage") || "",
  );

  const inFlight = useRef(false);

  const emailConfirmed = searchParams.get("verified") === "true";
  const passwordReset = searchParams.get("reset") === "true";

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlight.current) return;
    if (!identifier || !password) return;

    window.localStorage.removeItem("errorMessage");
    setStoredMessage("");
    setCredentialError(false);
    setEmailNotVerified(false);

    inFlight.current = true;
    setBusy(true);
    try {
      const token: Usuario.Token = await AuthorizationService.getToken(
        loginPayload(identifier, password),
      );

      if (!token.token) {
        setCredentialError(true);
        return;
      }

      AuthService.setAccessToken(token.token);

      const user: Usuario.Usuario = await AuthorizationService.getUser(
        token.token,
      );

      if (!user || !user.id) {
        setCredentialError(true);
        return;
      }

      setUser(user);
      // Semeia o cache que os guardas de rota leem, para a primeira navegação
      // depois do login não gastar outro GET /auth/{token}.
      setProfile((user.profile as Profile | undefined) ?? null);

      navigate(hasProfile(user.profile, PAID) ? "/home" : "/planos");
    } catch (error) {
      if (isEmailNotVerified(error)) {
        setEmailNotVerified(true);
      } else {
        setCredentialError(true);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <AuthFrame screen="login">
      <form className="login-form" aria-busy={busy} onSubmit={onSubmit}>
        <h1 className="login-form__title">Entrar</h1>
        <p className="login-form__subtitle">
          Que bom te ver de novo. Seu treino continua de onde parou.
        </p>

        {emailConfirmed ? (
          <p className="login-form__status" role="status">E-mail confirmado! Entre com suas credenciais.</p>
        ) : null}

        {passwordReset ? (
          <p className="login-form__status" role="status">Senha redefinida! Entre com suas novas credenciais.</p>
        ) : null}

        {storedMessage && !credentialError && !emailNotVerified ? (
          <div className="login-form__denial" role="alert">
            <CircleAlert
              className="login-form__denial-icon"
              aria-hidden="true"
              size={20}
            />
            <p>{storedMessage}</p>
          </div>
        ) : null}

        {credentialError ? (
          <div className="login-form__alert" id="login-error" role="alert">
            <CircleAlert
              className="login-form__alert-icon"
              aria-hidden="true"
              size={20}
            />
            <p>E-mail, usuário ou senha incorretos. Confira e tente de novo.</p>
          </div>
        ) : null}

        {emailNotVerified ? (
          <div className="login-form__verify">
            <p className="login-form__verify-title">E-mail não confirmado</p>
            <p className="login-form__verify-text">Confirme seu e-mail antes de entrar.</p>
            <Link className="login-form__verify-link" to="/verificar-email">Reenviar e-mail de confirmação →</Link>
          </div>
        ) : null}

        <div className="login-form__field">
          <label className="login-form__label" htmlFor="login-identifier">E-mail ou nome de usuário</label>
          <div className="login-form__control">
            <Mail className="login-form__icon" aria-hidden="true" size={20} />
            <input
              id="login-identifier"
              className="login-form__input"
              name="userName"
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              placeholder="seu@email.com ou usuário"
              required
              disabled={busy}
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              aria-invalid={credentialError ? true : undefined}
              aria-describedby={credentialError ? "login-error" : undefined}
            />
          </div>
        </div>

        <div className="login-form__field">
          <label className="login-form__label" htmlFor="login-password">Senha</label>
          <div className="login-form__control">
            <Lock className="login-form__icon" aria-hidden="true" size={20} />
            <input
              id="login-password"
              className="login-form__input"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Sua senha"
              required
              disabled={busy}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={credentialError ? true : undefined}
              aria-describedby={credentialError ? "login-error" : undefined}
            />
            <button
              type="button"
              className="login-form__reveal"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              aria-pressed={showPassword}
              disabled={busy}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? (
                <EyeOff aria-hidden="true" size={22} />
              ) : (
                <Eye aria-hidden="true" size={22} />
              )}
            </button>
          </div>
        </div>

        <Link className="login-form__forgot" to="/esqueci-senha">Esqueci a senha</Link>

        <button type="submit" className="login-form__submit" disabled={busy}>
          {busy ? (
            <LoaderCircle
              className="login-form__spinner"
              aria-hidden="true"
              size={20}
            />
          ) : null}
          {busy ? "Entrando…" : "Entrar"}
        </button>

        <div className="login-form__divider" />

        <div className="login-form__switch">
          <p className="login-form__switch-text">Ainda não tem conta?</p>
          <Link className="login-form__create" to="/registro">Criar conta</Link>
        </div>
      </form>
    </AuthFrame>
  );
}
